/*
 Аналитика витрины: единая точка отправки в Яндекс Метрику.

 Номер счётчика — только window.TAS_YM_COUNTER_ID (blocks/header.php ставит его
 из константы окружения); на dev вместо счётчика заглушка window.ym с журналом
 window.__ymLog — код здесь одинаков для всех окружений. Персональные данные
 (телефон, почта, имя, текст обращения) в параметры целей не передаются.

 goal(name, params, dedupeKey) — цель «Целевое событие» (reachGoal). dedupeKey —
   ключ уникальности бизнес-события (например 'lead:<RESULT_ID>'): повторный
   вызов с тем же ключом в этой вкладке (sessionStorage) не отправляется —
   защита от двойного клика, повторного рендера и возврата «назад».
 hit(url, title, referer) — просмотр страницы при SPA-переходе; сама Метрика
   pushState не видит. Отправляется только при смене адреса относительно
   последнего отправленного (перезагрузка данных той же страницы, например
   после сохранения профиля в ЛК, просмотром не считается).
 ecommerce(action, payload) — событие электронной коммерции через dataLayer
   (счётчик инициализирован с ecommerce:"dataLayer"): detail / add / remove /
   purchase, формат Метрики { ecommerce: { currencyCode, <action>: payload } }.
*/

import { vplSiteStore } from '../../store/vplstore';

const STORAGE_KEY = 'tas_ym_sent';
const CURRENCY = 'RUB';

let lastHitUrl = window.location.href;

function counterId() {
    return Number(window.TAS_YM_COUNTER_ID) || 0;
}

function callYm() {
    if (typeof window.ym !== 'function') {
        return;
    }
    window.ym(counterId(), ...arguments);
}

function readSent() {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
    } catch (e) {
        return [];
    }
}

function markSent(key) {
    try {
        const list = readSent();
        list.push(key);
        // журнал не растёт бесконечно: хранится последняя сотня ключей
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(-100)));
    } catch (e) {
        /* приватный режим или отключённое хранилище — дедупликация в пределах памяти вкладки */
        memorySent.add(key);
    }
}

const memorySent = new Set();

function alreadySent(key) {
    return memorySent.has(key) || readSent().includes(key);
}

export function goal(name, params = null, dedupeKey = null) {
    if (!name) {
        return false;
    }
    if (dedupeKey) {
        const key = name + '|' + dedupeKey;
        if (alreadySent(key)) {
            return false;
        }
        markSent(key);
        memorySent.add(key);
    }
    if (params && typeof params === 'object' && Object.keys(params).length) {
        callYm('reachGoal', name, params);
    } else {
        callYm('reachGoal', name);
    }
    return true;
}

export function hit(url = window.location.href, title = document.title, referer = lastHitUrl) {
    if (!url || url === lastHitUrl) {
        return false;
    }
    callYm('hit', url, { title: title || '', referer: referer || '' });
    lastHitUrl = url;
    return true;
}

export function ecommerce(action, payload) {
    if (!action || !payload) {
        return false;
    }
    window.dataLayer = window.dataLayer || [];
    const event = { ecommerce: { currencyCode: CURRENCY } };
    event.ecommerce[action] = payload;
    window.dataLayer.push(event);
    return true;
}

/*
 Микроцели «скопировал телефон / почту». Ссылки с классами js-copyphone /
 js-copymail рисуют компоненты Vue (шапка, подвал, контакты) и пересоздают их
 при перерисовках и SPA-переходах, поэтому обработчик — делегированный, один
 на документ: старый $('.js-copyphone').bind('copy') в подвале вешался лишь на
 узлы, существовавшие в момент выполнения, и ни одной цели не отправлял.
*/
export function initCopyGoals() {
    document.addEventListener('copy', function (event) {
        const el = event.target && event.target.nodeType === 1 ? event.target : (event.target && event.target.parentElement);
        if (!el || !el.closest) {
            return;
        }
        if (el.closest('.js-copyphone')) {
            goal('phone_copy');
        } else if (el.closest('.js-copymail')) {
            goal('email_copy');
        }
    });
}

let messengerGoalsInitialized = false;

/*
 SAIT-153: переход в мессенджер — отдельная микроцель, не принятая заявка.
 Один обработчик на документ обслуживает шапку, плавающие кнопки, контакты
 и карточку товара, в том числе после SPA-переходов. Рекламный источник
 визита сохраняется: ссылки не дополняются внутренними UTM-метками.
*/
export function initMessengerGoals() {
    if (messengerGoalsInitialized) return;
    messengerGoalsInitialized = true;

    document.addEventListener('click', function (event) {
        const el = event.target && event.target.nodeType === 1 ? event.target : (event.target && event.target.parentElement);
        const link = el && el.closest ? el.closest('a[data-messenger-channel]') : null;
        if (!link) return;

        const channels = { telegram: 'telegram', 'icon-tg': 'telegram', max: 'max', 'icon-max': 'max' };
        const channel = channels[link.getAttribute('data-messenger-channel')];
        if (channel !== 'telegram' && channel !== 'max') return;

        goal('messenger_click', Object.assign({
            channel: channel,
            placement: link.getAttribute('data-messenger-placement') || 'other',
        }, pageContext()));
    });
}

/*
 Контекст страницы для параметров целей: business_area — направление бизнеса
 (по ТЗ директолога: проектные/лидовые кампании против интернет-магазина),
 page_type — тип страницы. Разделы каталога живут в корне адреса
 (/kholodilnye-kamery/), поэтому тип берётся не только из пути, но и из состава
 страницы (компонент CatalogSectionPage в struct).
*/
export function pageContext() {
    const path = window.location.pathname || '/';
    let components = [];
    try {
        const struct = vplSiteStore().struct;
        const list = Array.isArray(struct) ? struct : Object.values(struct || {});
        components = list.map((x) => x && x.componentData && x.componentData.VUE_COMPONENT).filter(Boolean);
    } catch (e) { /* хранилище ещё не создано */ }

    let pageType = 'other';
    if (path === '/') pageType = 'main';
    else if (path.indexOf('/product/') === 0) pageType = 'product';
    else if (path.indexOf('/catalog/') === 0 || path.indexOf('/brendy/') === 0 || components.includes('CatalogSectionPage')) pageType = 'catalog';
    else if (path.indexOf('/osnascheniye-pod-klyuch/') === 0) pageType = 'osnashchenie';
    else if (path.indexOf('/proyektirovaniye-pod-klyuch/') === 0) pageType = 'proektirovanie';
    else if (path.indexOf('/uslugi/') === 0) pageType = 'services';
    else if (path.indexOf('/vypolnennyye-proyekty/') === 0) pageType = 'projects';
    else if (path.indexOf('/kontakty/') === 0) pageType = 'contacts';
    else if (path.indexOf('/cart/') === 0) pageType = 'cart';
    else if (path.indexOf('/lk/') === 0) pageType = 'lk';
    else if (path.indexOf('/novosti/') === 0 || path.indexOf('/aktsii/') === 0 || path.indexOf('/blog/') === 0) pageType = 'content';

    const areaByType = {
        main: 'main', product: 'catalog', catalog: 'catalog', cart: 'catalog',
        osnashchenie: 'osnashchenie', proektirovanie: 'proektirovanie', services: 'services', projects: 'services',
    };
    return { page_type: pageType, business_area: areaByType[pageType] || 'other' };
}

/*
 Главная лидовая цель по ТЗ директолога. Вызывается ТОЛЬКО после ответа
 сервера STATUS:"OK" с номером созданной записи (RESULT_ID веб-формы, ID
 вопроса/подписки): нажатие кнопки, ошибка валидации, сбой сети целью не
 считаются. Ключ дедупликации — тип формы + номер записи: повторный рендер или
 возврат «назад» второй цели не дают. extra — технические параметры
 (form_theme, product_id); персональные данные сюда не передавать.
*/
export function leadGoal(formType, response, extra = null) {
    if (!formType || !response || response.STATUS !== 'OK') {
        return false;
    }
    const recordId = Number(response.RESULT_ID || response.ID) || 0;
    if (!recordId) {
        return false;
    }
    const params = Object.assign({ form_type: formType }, pageContext(), extra || {});
    return goal('lead_success', params, formType + ':' + recordId);
}

/*
 E-commerce add/remove по ответу /ajax/basket.php: дельту считает сервер
 (TasAnalytics::withBasketDiff → ключ ANALYTICS {add, remove} с товарами в
 формате Метрики), клиент только кладёт её в dataLayer — количество и цена
 всегда равны фактической корзине, а все вызывающие (плитка, карточка,
 корзина в шапке, страница /cart/) обслуживаются одинаково.
*/
export function basketEvents(basketData) {
    const a = basketData && basketData.ANALYTICS;
    if (!a) {
        return false;
    }
    let sent = false;
    if (Array.isArray(a.add) && a.add.length) {
        sent = ecommerce('add', { products: a.add }) || sent;
    }
    if (Array.isArray(a.remove) && a.remove.length) {
        sent = ecommerce('remove', { products: a.remove }) || sent;
    }
    return sent;
}

/* E-commerce detail — просмотр карточки товара (ITEM из JSON страницы) */
export function productDetail(item) {
    if (!item || !item.ID) {
        return false;
    }
    return ecommerce('detail', {
        products: [{
            id: String(item.ID),
            name: item.NAME || '',
            price: Number(item.PRICE && item.PRICE.PRICE) || 0,
            brand: (item.BRAND && item.BRAND.NAME) || '',
            category: item.SECTION_PATH || '',
        }],
    });
}

/*
 Атрибуция первого касания: при первом заходе за 30 дней в cookie tas_attr
 пишется JSON {utm_source…utm_term, yclid, referrer, landing, ts}. Сервер
 (TasAnalytics::attribution) читает её при создании заказа и заявки и
 сохраняет в свойства заказа / скрытый вопрос формы — так рекламный клик
 связывается с заказом и оплатой (ТЗ директолога §2.7). Повторные визиты без
 меток cookie не перезаписывают (первое касание сохраняется); визит с новыми
 UTM/yclid — перезаписывают (последний рекламный клик). ClientID Метрики
 отдельно не пишется: он равен её cookie _ym_uid, сервер читает её напрямую.
*/
const ATTR_COOKIE = 'tas_attr';
const ATTR_DAYS = 30;

function readCookie(name) {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
}

export function initAttribution() {
    let params;
    try {
        params = new URLSearchParams(window.location.search);
    } catch (e) {
        return false;
    }
    const cut = (v) => String(v || '').trim().slice(0, 200);
    const fresh = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'yclid'].forEach((k) => {
        const v = cut(params.get(k));
        if (v) {
            fresh[k] = v;
        }
    });
    const hasClick = Object.keys(fresh).length > 0;
    const existing = readCookie(ATTR_COOKIE);
    if (existing && !hasClick) {
        return false;
    }
    let referrer = '';
    try {
        const ref = document.referrer ? new URL(document.referrer) : null;
        referrer = ref && ref.hostname !== window.location.hostname ? cut(ref.href) : '';
    } catch (e) { /* пустой или кривой referrer */ }
    const data = Object.assign(fresh, {
        referrer: referrer,
        landing: cut(window.location.pathname + window.location.search),
        ts: new Date().toISOString(), // UTC с маркером Z — часовой пояс посетителя неизвестен
    });
    try {
        document.cookie = ATTR_COOKIE + '=' + encodeURIComponent(JSON.stringify(data))
            + '; path=/; max-age=' + (ATTR_DAYS * 86400) + '; SameSite=Lax'
            + (window.location.protocol === 'https:' ? '; Secure' : '');
    } catch (e) {
        return false;
    }
    return true;
}

